/**
 * Control-testing hooks (P279) -- ToD/ToI/ToE per control + the Test of
 * Design Engine analyser over the P278-mapped documents.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { invalidateCoveringDocuments } from "framework/api/useDocumentGap";
import type {
  ControlTest,
  ControlTestList,
  ControlTestUpsertIn,
  TestDesignResult,
} from "shared/api/types";

export const controlTestKeys = {
  list: (cycleId: string, controlId: string) =>
    ["control-tests", cycleId, controlId] as const,
};

export function useControlTests(
  cycleId: string,
  controlId: string,
): UseQueryResult<ControlTestList, ApiError> {
  return useQuery({
    queryKey: controlTestKeys.list(cycleId, controlId),
    queryFn: () =>
      api.get<ControlTestList>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/tests`,
      ),
    enabled: Boolean(cycleId && controlId),
  });
}

export function useUpsertControlTest(
  cycleId: string,
  controlId: string,
): UseMutationResult<
  ControlTest,
  ApiError,
  { testType: string; body: ControlTestUpsertIn }
> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice in two wordings.
    meta: { handled: true },
    mutationFn: ({ testType, body }) =>
      api.put<ControlTest>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/tests/${testType}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: controlTestKeys.list(cycleId, controlId),
      });
      void qc.invalidateQueries({ queryKey: ["framework-assessments"] });
    },
  });
}

/**
 * Round 13 A2 -- accepting the design test's proposal.
 *
 * Separate mutation rather than a flag on the one above, because the two
 * are different acts by different authorities: one asks the Engine, the
 * other moves the bank's recorded compliance position.
 */
export function useAcceptTestDesign(
  cycleId: string,
  controlId: string,
): UseMutationResult<TestDesignResult, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    meta: { handled: true },
    mutationFn: () =>
      api.post<TestDesignResult>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/test-design/accept`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: controlTestKeys.list(cycleId, controlId),
      });
      void qc.invalidateQueries({ queryKey: ["framework-assessments"] });
    },
  });
}

/**
 * Round 14 A1 -- declining the design test's proposal. A decline is a
 * record (audited with what was declined), not closing a panel: before
 * this the proposal went on existing and the next hand-entered test
 * folded it into the status.
 */
export function useDeclineTestDesign(
  cycleId: string,
  controlId: string,
): UseMutationResult<ControlTest, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    meta: { handled: true },
    mutationFn: () =>
      api.post<ControlTest>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/test-design/decline`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: controlTestKeys.list(cycleId, controlId),
      });
    },
  });
}

export function useTestDesign(
  cycleId: string,
  controlId: string,
): UseMutationResult<TestDesignResult, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    // E1 (round 8) -- this mutation's caller awaits `mutateAsync` and
    // catches, which react-query cannot see. Without this the global
    // failure sink announces the server's detail as well, so one
    // failure reaches the reader twice in two wordings.
    meta: { handled: true },
    mutationFn: () =>
      api.post<TestDesignResult>(
        `/v1/framework/cycles/${cycleId}/controls/${controlId}/test-design`,
        {},
      ),
    onSuccess: () => {
      invalidateCoveringDocuments(qc);
      void qc.invalidateQueries({
        queryKey: controlTestKeys.list(cycleId, controlId),
      });
      void qc.invalidateQueries({ queryKey: ["framework-assessments"] });
    },
  });
}
